import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Label, Popover, PopoverContent, PopoverTrigger, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { programRoleAssignmentHooks } from '../../Programs/relationshipHooks';
/**
 * Combobox over a program's INTERNAL role assignments (the Roles tab), for
 * picking an owner/assignee from named personnel rather than free text or a raw
 * contact. Value is the assignment's contact id; onChange also returns the
 * display name so the caller keeps the legacy free-text column (owner /
 * assigned_to) in sync — the home strip prints that text, so an id-only save
 * would blank it. Read-only over existing assignments: someone not on the
 * program must be added under Roles first (a role write is the "programs" authz
 * area, not the "quality" area these risk/issue forms write).
 */ export function RolePicker({ id, label, helpText, programId, value, onChange, onClear, fallbackLabel, testid }) {
    const [open, setOpen] = useState(false);
    const { data, isLoading } = programRoleAssignmentHooks.useList({
        programId
    });
    const roles = data?.items ?? [];
    const selected = roles.find((r)=>r.contactId === value);
    const triggerLabel = selected?.contactDisplayName || fallbackLabel || 'Select a role…';
    const handleSelect = (contactId, name)=>{
        onChange(contactId, name);
        setOpen(false);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: id,
                children: label
            }),
            helpText && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: helpText
            }),
            /*#__PURE__*/ _jsxs(Popover, {
                open: open,
                onOpenChange: setOpen,
                children: [
                    /*#__PURE__*/ _jsx(PopoverTrigger, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsxs(SpecraButton, {
                            id: id,
                            type: "button",
                            variant: "secondary",
                            role: "combobox",
                            "aria-expanded": open,
                            className: "h-10 w-full justify-between bg-background font-normal",
                            "data-testid": testid ?? Testids.deliveryShared.fkCombobox,
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "truncate text-left",
                                    children: triggerLabel
                                }),
                                /*#__PURE__*/ _jsx(ChevronsUpDown, {
                                    className: "ml-2 h-4 w-4 shrink-0 opacity-50"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(PopoverContent, {
                        className: "w-[var(--radix-popover-trigger-width)] p-0",
                        align: "start",
                        children: /*#__PURE__*/ _jsxs(Command, {
                            label: `Search ${label}`,
                            children: [
                                /*#__PURE__*/ _jsx(CommandInput, {
                                    placeholder: "Search roles by name…",
                                    "data-testid": Testids.deliveryShared.fkComboboxSearch
                                }),
                                /*#__PURE__*/ _jsxs(CommandList, {
                                    children: [
                                        isLoading && /*#__PURE__*/ _jsx(CommandEmpty, {
                                            className: "py-4 text-center text-xs",
                                            children: "Loading…"
                                        }),
                                        !isLoading && roles.length === 0 && /*#__PURE__*/ _jsx(CommandEmpty, {
                                            className: "py-4 text-center text-xs text-muted-foreground",
                                            children: "No internal roles on this program yet. Add them under Roles."
                                        }),
                                        (value || fallbackLabel) && onClear && /*#__PURE__*/ _jsx(CommandGroup, {
                                            children: /*#__PURE__*/ _jsx(CommandItem, {
                                                value: "__clear__",
                                                keywords: [
                                                    'clear',
                                                    'none',
                                                    'remove'
                                                ],
                                                onSelect: ()=>{
                                                    onClear();
                                                    setOpen(false);
                                                },
                                                children: /*#__PURE__*/ _jsx("span", {
                                                    className: "text-muted-foreground",
                                                    children: "Clear selection"
                                                })
                                            })
                                        }),
                                        !isLoading && roles.length > 0 && /*#__PURE__*/ _jsx(CommandGroup, {
                                            children: roles.map((r)=>/*#__PURE__*/ _jsx(CommandItem, {
                                                    // cmdk keys selection/filtering off value, so it must be
                                                    // unique per row — the assignment id, not the contact id,
                                                    // since one person can hold two internal roles. onSelect
                                                    // still stores the contact id.
                                                    value: r.id,
                                                    keywords: [
                                                        r.contactDisplayName,
                                                        r.roleTypeLabel
                                                    ],
                                                    "data-testid": Testids.deliveryShared.fkComboboxOption,
                                                    "data-row-id": r.contactId,
                                                    onSelect: ()=>handleSelect(r.contactId, r.contactDisplayName),
                                                    children: r.roleTypeLabel ? `${r.contactDisplayName} — ${r.roleTypeLabel}` : r.contactDisplayName
                                                }, r.id))
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
}
